import React, { useState, useEffect } from "react";
import {
  Form,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
  InputGroup,
  Button,
  Modal,
  Spinner,
  ButtonGroup,
  ToggleButton,
} from "react-bootstrap";
import ObjectFieldSelector from "../fieldselector/ObjectFieldSelector";
import ObjectConfigApi from "../../api/ObjectConfigApi";
import LookupLayout from "../layout/LookupLayout";
import moment from "moment";

export default function WorkflowCreateTask({
  config,
  onChange,
  objectName,
  readOnly,
}) {
  const [showFieldSelector, setShowFieldSelector] = useState(false);
  const [taskFields, setTaskFields] = useState({ main: [], description: null });
  const [picklistValues, setPicklistValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Owner assignment mode: 'user' or 'field'
  const [ownerMode, setOwnerMode] = useState(
    config?.ownerid && config.ownerid.includes("{{") ? "field" : "user"
  );

  // Fields to display in workflow task creation (description will be rendered last)
  const workflowFields = [
    "title",
    "ownerid",
    "priority",
    "status",
    "type",
  ];

  // Fetch task fields and picklist values
  useEffect(() => {
    fetchTaskFields();
  }, []);

  const fetchTaskFields = async () => {
    try {
      setLoading(true);
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields("task");

      console.log("Task fields response:", response);

      if (response.success && response.data) {
        const allFields = response.data.fields || [];

        // Filter to show only workflow-relevant fields (including description for later)
        const filteredFields = allFields.filter(field =>
          workflowFields.includes(field.field_name) || field.field_name === "description"
        );

        // Sort fields in the order we want to display them (excluding description)
        const sortedFields = workflowFields
          .map(fieldName => filteredFields.find(f => f.field_name === fieldName))
          .filter(Boolean); // Remove undefined entries

        // Store all fields including description separately
        const fieldsWithDescription = {
          main: sortedFields,
          description: filteredFields.find(f => f.field_name === "description")
        };

        console.log("Filtered task fields:", fieldsWithDescription);
        setTaskFields(fieldsWithDescription);

        // Extract picklist values - prefer picklist_values_data (active values from backend)
        // or filter picklist_values by is_active
        const picklistValuesMap = {};
        filteredFields.forEach(field => {
          if (field.data_type === "picklist" || field.data_type === "multipicklist") {
            let values = null;
            let needsFiltering = false;

            // First try picklist_values_data (new optimized field with only active values from backend)
            if (field.picklist_values_data && Array.isArray(field.picklist_values_data)) {
              values = field.picklist_values_data;
              // picklist_values_data is already filtered by backend (WHERE pv.is_active = true)
              needsFiltering = false;
            }
            // Fallback to picklist_values (may include inactive values)
            else if (field.picklist_values) {
              if (Array.isArray(field.picklist_values)) {
                values = field.picklist_values;
                needsFiltering = true; // Need to filter by is_active
              } else if (typeof field.picklist_values === "string") {
                try {
                  values = JSON.parse(field.picklist_values);
                  needsFiltering = true; // Need to filter by is_active
                } catch (e) {
                  console.error(`Failed to parse picklist values for ${field.field_name}:`, e);
                  values = null;
                }
              }
            }

            // Filter to only include active values if needed
            if (values && Array.isArray(values)) {
              let activeValues = values;

              if (needsFiltering) {
                // Filter to only include active values
                activeValues = values.filter(val => {
                  // If is_active property exists, only include active values
                  if (val && typeof val === 'object' && 'is_active' in val) {
                    return val.is_active === true;
                  }
                  // If is_active doesn't exist, include all (for backward compatibility)
                  return true;
                });
              }

              if (activeValues.length > 0) {
                picklistValuesMap[field.field_name] = activeValues;
              }
            }
          }
        });

        console.log("Picklist values:", picklistValuesMap);
        setPicklistValues(picklistValuesMap);
      }
    } catch (err) {
      console.error("Error fetching task fields:", err);
      setError("Failed to load task fields");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldSelected = (field) => {
    if (field?.field_name) {
      onChange("ownerid", `{{${field.field_name}}}`);
    }
    setShowFieldSelector(false);
  };

  const handleOwnerModeChange = (mode) => {
    setOwnerMode(mode);
    // Clear ownerid and ownerName when switching modes
    onChange("ownerid", "");
    onChange("ownerName", "");
  };

  const handleUserLookupChange = (userId, userName) => {
    onChange("ownerid", userId);
    onChange("ownerName", userName || ""); // Store display name for later display
  };

  // Extract field name from merge syntax like "{{fieldname}}"
  const getSelectedFieldName = () => {
    if (config?.ownerid && config.ownerid.includes("{{")) {
      const match = config.ownerid.match(/\{\{(.+?)\}\}/);
      return match ? match[1].trim() : null;
    }
    return null;
  };

  // Render field based on data type
  const renderField = (field) => {
    const fieldName = field.field_name;
    const fieldLabel = field.field_label;
    const dataType = field.data_type;
    const isRequired = field.is_required;
    const value = config?.[fieldName] || "";

    // Special handling for ownerid field
    if (fieldName === "ownerid") {
      return (
        <Col lg={6} key={fieldName}>
          <Form.Group>
            <Form.Label>
              {fieldLabel}
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip>
                    Choose how to assign the task owner:
                    <br />
                    <strong>Select User:</strong> Pick a specific user
                    <br />
                    <strong>Use Field:</strong> Dynamically assign using{" "}
                    <strong>&#123;&#123;fieldname&#125;&#125;</strong> syntax
                  </Tooltip>
                }
              >
                <i className="fa fa-info-circle mx-2 text-muted"></i>
              </OverlayTrigger>
            </Form.Label>

            {/* Mode Toggle */}
            <ButtonGroup className="mb-2 d-flex">
              <ToggleButton
                id="owner-mode-user"
                type="radio"
                variant={ownerMode === "user" ? "primary" : "outline-primary"}
                name="ownerMode"
                value="user"
                checked={ownerMode === "user"}
                onChange={() => handleOwnerModeChange("user")}
                disabled={readOnly}
                size="sm"
              >
                <i className="fa fa-user me-1"></i> Select User
              </ToggleButton>
              <ToggleButton
                id="owner-mode-field"
                type="radio"
                variant={ownerMode === "field" ? "primary" : "outline-primary"}
                name="ownerMode"
                value="field"
                checked={ownerMode === "field"}
                onChange={() => handleOwnerModeChange("field")}
                disabled={readOnly}
                size="sm"
              >
                <i className="fa fa-code me-1"></i> Use Field
              </ToggleButton>
            </ButtonGroup>

            {/* Render based on selected mode */}
            {ownerMode === "user" ? (
              <LookupLayout
                objectName="user"
                searchFields="email,firstname,lastname"
                displayFields="firstname,lastname"
                value={value && !value.includes("{{") ? value : ""}
                defaultLabel={config?.ownerName || ""}
                onChange={handleUserLookupChange}
                disabled={readOnly}
                placeholder="Search for a user..."
                isRequired={isRequired && !readOnly}
              />
            ) : (
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Enter field name like {{fieldname}}"
                  value={value}
                  onChange={(e) => onChange(fieldName, e.target.value)}
                  required={isRequired && !readOnly}
                  disabled={readOnly}
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowFieldSelector(true)}
                  disabled={readOnly}
                  size="sm"
                >
                  <i className="fa fa-search me-1"></i> Select Field
                </Button>
              </InputGroup>
            )}
          </Form.Group>
        </Col>
      );
    }

    // Handle different data types
    switch (dataType) {
      case "picklist":
        const options = picklistValues[fieldName] || [];
        return (
          <Col lg={6} key={fieldName}>
            <Form.Group>
              <Form.Label>{fieldLabel}</Form.Label>
              <Form.Select
                value={value}
                onChange={(e) => onChange(fieldName, e.target.value)}
                required={isRequired && !readOnly}
                disabled={readOnly}
              >
                <option value="">-Select {fieldLabel}-</option>
                {options.map((opt) => {
                  const optionKey = opt.key || opt.value_key;
                  const optionValue = opt.value || opt.value_label;
                  return (
                    <option key={optionKey} value={optionKey}>
                      {optionValue}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>
          </Col>
        );

      case "textarea":
      case "text":
        const colSize = dataType === "textarea" ? 12 : 6;
        return (
          <Col lg={colSize} key={fieldName}>
            <Form.Group>
              <Form.Label>{fieldLabel}</Form.Label>
              <Form.Control
                as={dataType === "textarea" ? "textarea" : "input"}
                type={dataType === "textarea" ? undefined : "text"}
                rows={dataType === "textarea" ? 2 : undefined}
                value={value}
                onChange={(e) => onChange(fieldName, e.target.value)}
                required={isRequired && !readOnly}
                disabled={readOnly}
              />
            </Form.Group>
          </Col>
        );

      default:
        return (
          <Col lg={6} key={fieldName}>
            <Form.Group>
              <Form.Label>{fieldLabel}</Form.Label>
              <Form.Control
                type="text"
                value={value}
                onChange={(e) => onChange(fieldName, e.target.value)}
                required={isRequired && !readOnly}
                disabled={readOnly}
              />
            </Form.Group>
          </Col>
        );
    }
  };

  if (loading) {
    return (
      <div className="text-center p-3">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading task fields...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        {error}
      </div>
    );
  }

  return (
    <>
      <Row>
        {/* Render main dynamic task fields (title, ownerid, priority, status, type) */}
        {taskFields.main && taskFields.main.map((field) => renderField(field))}

        {/* Custom field: Complete in (Days) - appears after Type */}
        <Col lg={6}>
          <Form.Group>
            <Form.Label>
              Complete in (Days)
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip>
                    Number of days from task creation to completion. This will
                    automatically calculate the due date when the task is created.
                  </Tooltip>
                }
              >
                <i className="fa fa-info-circle mx-2 text-muted"></i>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={config?.daysToComplete || ""}
              onChange={(e) =>
                onChange("daysToComplete", parseInt(e.target.value, 10) || "")
              }
              disabled={readOnly}
              placeholder="Enter number of days"
            />
            {config?.daysToComplete && (
              <Form.Text className="text-muted">
                Due date will be:{" "}
                {moment()
                  .add(parseInt(config.daysToComplete, 10), "days")
                  .format("MMM DD, YYYY")}
              </Form.Text>
            )}
          </Form.Group>
        </Col>

        {/* Description field - rendered last */}
        {taskFields.description && renderField(taskFields.description)}
      </Row>

      {/* Field Selector Modal */}
      <Modal
        show={showFieldSelector}
        onHide={() => setShowFieldSelector(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Select User Lookup Field for Assign User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ObjectFieldSelector
            objectName={objectName}
            multiSelect={false}
            onSelect={handleFieldSelected}
            fieldTypeFilter="lookup"
            referenceObjectFilter="user"
            defaultSelectedFieldName={getSelectedFieldName()}
          />
        </Modal.Body>
      </Modal>
    </>
  );
}
